"use client"

// @ts-ignore
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { CalendarProvider } from "@/contexts/CalendarContext";
import { CalendarDataProvider } from "@/contexts/CalendarDataProvider";
import { SelectedStaffProvider } from "@/contexts/SelectedStaffContext";
import { AppointmentModalProvider } from "@/contexts/AppointmentModalContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { AuthProvider } from "@/features/auth/AuthContext";
import MainLayout from "@/layouts/MainLayout";
import DashboardPage from "@/features/dashboard/pages/DashboardPage";
import { useAuth } from "@/features/auth/AuthContext";
import SsoPage from "@/features/sso/SsoPage";
import DemoBanner from "@/features/demo/DemoBanner";
import { SessionExpiredBanner } from "@/components/SessionExpiredBanner";
import { Toaster } from "@/components/ui/sonner";
import { NotificationsPanel } from "@/components/custom/NotificationsPanel";
import { InboxBridge } from "@/components/inbox/InboxBridge";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const LOGIN_URL = "https://id.boona.app/login";

/**
 * Shows a centered login prompt when user is not authenticated
 */
function LoginPrompt() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="text-center space-y-6 p-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t("auth.welcome", "Welcome to Boona")}
        </h1>
        <p className="text-gray-600 max-w-sm">
          {t("auth.login_prompt", "Please log in to access your dashboard")}
        </p>
        <Button
          size="lg"
          asChild
        >
          <a href={LOGIN_URL}>
            {t("auth.login_button", "Log in")}
          </a>
        </Button>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
      {/* Global toasts (demo read-only guard notices, etc.) */}
      <Toaster position="top-center" richColors closeButton />
    </ThemeProvider>
  );
}

// Split out the main content to use auth gating
function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const region = getAuthRegistry().getRegion();

  // Always-mounted session-expired dialog
  const Banner = <SessionExpiredBanner />;

  if (loading) {
    return null; // or a spinner
  }

  // Handle SSO landing route (always accessible)
  if (window.location.pathname === "/sso") {
    return (
      <>
        {Banner}
        <Routes>
          <Route path="/sso" element={<SsoPage />} />
        </Routes>
      </>
    );
  }

  // If not authenticated or no region, show login prompt
  if (!isAuthenticated || !region) {
    return <LoginPrompt />;
  }

  // Only mount main app providers/layouts when authenticated
  return (
    <>
      {Banner}
      {/* Demo sessions only — no-ops for real customers. The demo had no route back to
          signup at all, which is where the paid funnel was ending. */}
      <DemoBanner />
      <NotificationsProvider>
        <AppointmentModalProvider>
          <CalendarProvider>
            <SelectedStaffProvider>
              <CalendarDataProvider>
                <Routes>
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<DashboardPage />} />
                  </Route>
                  {/* SSO landing route (dashboard -> frontend) */}
                  <Route path="/sso" element={<SsoPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                {/* Sliding panel — a Radix Sheet (portals to body), so its
                    position here is purely about context access. It lives
                    INSIDE CalendarProvider so its waiting-list tab can jump
                    the calendar (useCalendar); AppointmentModalProvider is
                    still an ancestor, so opening appointments also works. */}
                <NotificationsPanel />
                {/* Boona Direct: the chat tab asks this tab to open an appointment or
                    to book a client (src/lib/inbox/crossTab.ts) */}
                <InboxBridge />
              </CalendarDataProvider>
            </SelectedStaffProvider>
          </CalendarProvider>
        </AppointmentModalProvider>
      </NotificationsProvider>
    </>
  );
}

export default App;
